import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageConfig from '@/component/layout/PageConfig';
import SearchBar from '@/component/input/SearchBar';
import BorderButton from '@/component/button/BorderButton';
import SolidButton from '@/component/button/SolidButton';
import Pagination from '@/component/pagination/Pagination';
import BCGSessionModal, { BCGSessionModalRef } from '@/component/mentalHealth/BCGSessionModal';
import useQueryGetUserDetail from '@/api/auth/getUser';
import useDebounce from '@/util/hook/useDebounce';
import { isEmptyArray, isNotEmptyArray } from '@/util/array';
import {
  useQueryGetMindHealthDirectory,
  useQueryGetMindHealthReports,
  useQueryGetMindHealthReport,
  MindHealthAnalysisState,
} from '@/api/mentalHealth/mindHealthReport';
import { formatRounded } from '@/util/mentalHealthFormat';
import { useAppLocale } from '@/locales/locale';
import { useQueryClient } from '@tanstack/react-query';
import { QueryCacheKeys } from '@/api/queryKeys';
import { SymptomPredictionCard } from '@/component/mentalHealth/SymptomPredictionCard';
import { Line } from 'react-chartjs-2';
import ArrowLeft from '@/assets/ic_arrow_left.svg?react';
import ArrowRight from '@/assets/ic_arrow_right.svg?react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
} from 'chart.js';
import 'chartjs-adapter-date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

const PAGE_SIZE = 10;
/** 서버 쿼리 파라미터와 값을 맞춰 둔다 — 탭을 그대로 넘기면 된다. */
type HealthMemberFilter = 'analyzed' | 'insufficient' | 'unmeasured';

// 서버 목록 행을 화면이 쓰는 모양으로 옮긴 것
interface UserHealthData {
  id: number;
  name: string;
  gender: number;
  birthDate: string;
  age?: number;
  healthScore?: number;
  healthStatus?: string;
  stressLevel?: string;
  lastAnalysisDate?: string;
  analysisState: MindHealthAnalysisState;
  hasAnalysisData: boolean;
  hasBackendData: boolean;
  sessionCount: number;
}

const MentalHealthDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { intlLocale } = useAppLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const lastWrittenSearchRef = useRef('');
  const isAutomaticSelectionRef = useRef(!searchParams.has('id'));
  const sessionModalRef = useRef<BCGSessionModalRef>(null);

  const [search, setSearch] = useState('');
  const [userId, setUserId] = useState(Number(searchParams.get('id')) || 0);
  const [currentPage, setCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [isListOnlyMode, setIsListOnlyMode] = useState(false);
  const [isUserListCollapsed, setIsUserListCollapsed] = useState(false);
  // 기본 목록은 전체 회원을 표시하되, 아래 정렬에서 분석 완료 회원을 항상 먼저 배치한다.
  // 선택 없음(null)이 곧 전체다. 같은 버튼을 다시 누르면 해제된다.
  const [memberFilter, setMemberFilter] = useState<HealthMemberFilter | null>(null);

  const debouncedSearchText = useDebounce(search, 500);

  // 목록·통계·필터·정렬·페이지네이션을 서버가 한 번에 준다.
  // 예전에는 회원 4,673명에게 세션 API 를 한 명씩 쏘고(468번의 왕복) 그 결과를
  // localStorage 점수와 합쳐 이 화면을 만들었다.
  const { data: directory, isLoading } = useQueryGetMindHealthDirectory({
    pageNum: currentPage,
    pageSize: PAGE_SIZE,
    query: debouncedSearchText || undefined,
    filter: memberFilter ?? 'all',
    sortBy: 'analyzed:desc',
  });

  // 선택된 회원의 리포트 이력. 요약이 인라인이라 행마다 따로 불러올 필요가 없다.
  const { data: reportHistory } = useQueryGetMindHealthReports(
    userId,
    { pageNum: 1, pageSize: 10, sortBy: 'measuredAt:desc' },
    userId > 0
  );
  const recentAnalyses = reportHistory?.data || [];
  // 건강 정보는 점수가 나온 가장 최근 회차로 보여준다.
  // 최근 측정이 품질 부족이었다고 해서 이전의 정상 결과까지 가릴 이유는 없다.
  const latestScored =
    recentAnalyses.find(item => Number.isFinite(item.summary.mindScore)) || null;

  // 증상 예측 그리드만 전체 리포트가 필요하다. 나머지는 목록 요약으로 그린다.
  const { data: latestReportDetail } = useQueryGetMindHealthReport(
    userId,
    latestScored?.sessionId,
    !!latestScored
  );

  const counts = directory?.counts;
  const totalPages = directory?.pagination.totalPages || 0;

  const paginatedUsersWithSessions = useMemo<UserHealthData[]>(() => {
    if (!directory) return [];

    return directory.data.map((row) => {
      // 서버가 점수 있는 리포트를 우선해 싣는다. 없으면 가장 최근 리포트가 온다.
      const summary = row.latestReport?.summary;
      const birthYear = row.birthDate ? new Date(row.birthDate).getFullYear() : null;

      return {
        id: row.protecteeId,
        name: row.name,
        gender: row.gender || 0,
        birthDate: row.birthDate || '',
        age: birthYear ? new Date().getFullYear() - birthYear : undefined,
        healthScore: Number.isFinite(summary?.mindScore)
          ? Math.round(summary!.mindScore!)
          : undefined,
        healthStatus: summary?.decisionLabel || undefined,
        stressLevel: summary?.stressCategory || undefined,
        lastAnalysisDate: row.latestReport?.measuredAt || undefined,
        analysisState: row.analysisState,
        hasAnalysisData: row.analysisState !== 'UNMEASURED',
        hasBackendData: row.reportCount > 0,
        sessionCount: row.reportCount,
      };
    });
  }, [directory]);

  const paginatedUsers = paginatedUsersWithSessions;

  // 목록에 없는 회원이 ?id= 로 지목될 수 있다 (다른 페이지·다른 필터에서 온 링크).
  // 그때만 단건 조회로 이름·성별·생년을 채운다.
  const selectedUserInPage = paginatedUsersWithSessions.find(u => u.id === userId);
  const { data: fallbackUser } = useQueryGetUserDetail(
    userId > 0 && !selectedUserInPage ? userId : undefined
  );

  const selectedUser = useMemo<UserHealthData | undefined>(() => {
    if (selectedUserInPage) return selectedUserInPage;
    if (!fallbackUser || userId <= 0) return undefined;

    const backendSessionCount = reportHistory?.pagination.totalEntries || 0;
    const birthYear = fallbackUser.birthDate
      ? new Date(fallbackUser.birthDate).getFullYear()
      : null;

    return {
      id: fallbackUser.id,
      name: fallbackUser.name,
      gender: fallbackUser.gender || 0,
      birthDate: fallbackUser.birthDate || '',
      age: birthYear ? new Date().getFullYear() - birthYear : undefined,
      analysisState: backendSessionCount > 0 ? 'ANALYZED' : 'UNMEASURED',
      hasAnalysisData: backendSessionCount > 0,
      hasBackendData: backendSessionCount > 0,
      sessionCount: backendSessionCount,
    };
  }, [selectedUserInPage, fallbackUser, userId, reportHistory]);




  // 첫 번째 사용자 자동 선택.
  // 서버가 정렬된 페이지를 한 번에 주므로 목록이 도중에 재정렬되지 않는다 —
  // 예전에는 세션 조회가 10개씩 도착할 때마다 첫 회원을 계속 다시 골랐다.
  useEffect(() => {
    const firstUser = paginatedUsersWithSessions[0];
    if (!isListOnlyMode && firstUser && userId <= 0) {
      isAutomaticSelectionRef.current = true;
      setUserId(firstUser.id);
    }
  }, [paginatedUsersWithSessions, userId, isListOnlyMode]);

  // 검색어 변경 시 첫 페이지로
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText]);

  const handleMemberFilterChange = (filter: HealthMemberFilter) => {
    isAutomaticSelectionRef.current = true;
    setMemberFilter(current => (current === filter ? null : filter));
    setCurrentPage(1);
    setUserId(0);
    setIsListOnlyMode(false);
  };

  // 메뉴를 다시 눌러 쿼리 파라미터가 제거되면 이전 선택을 버리고,
  // 정렬된 첫 회원(분석 완료 우선)을 다시 자동 선택한다.
  const searchParamsKey = searchParams.toString();
  useEffect(() => {
    if (lastWrittenSearchRef.current && lastWrittenSearchRef.current === searchParamsKey) {
      lastWrittenSearchRef.current = '';
      return;
    }

    const incomingParams = new URLSearchParams(searchParamsKey);
    const requestedUserId = Number(incomingParams.get('id')) || 0;
    const requestedPage = Number(incomingParams.get('page')) || 1;
    isAutomaticSelectionRef.current = requestedUserId <= 0;
    setUserId(requestedUserId);
    setCurrentPage(requestedPage);
    setIsListOnlyMode(false);
  }, [searchParamsKey]);

  // 화면 내부 선택 상태를 URL에 반영한다.
  useEffect(() => {
    const params = new URLSearchParams();
    if (currentPage > 1) params.set('page', currentPage.toString());
    if (userId > 0) params.set('id', userId.toString());

    lastWrittenSearchRef.current = params.toString();
    setSearchParams(params, { replace: true });
  }, [userId, currentPage, setSearchParams]);

  const handleUploadSuccess = () => {
    // 서버가 리포트를 소유하므로 캐시만 비우면 목록·이력·상세가 함께 갱신된다.
    queryClient.invalidateQueries({ queryKey: QueryCacheKeys.mentalHealth.keyId });
    setMemberFilter(null);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // 선택된 회원의 최신 리포트 상세 페이지로 이동
  const handleViewDetailReport = () => {
    if (!latestScored || !userId) return;
    navigate(`/mental-health/protectee/${userId}/reports/${latestScored.sessionId}`);
  };

  // 분석 기록 클릭 핸들러. 리포트는 이미 서버에 있으므로 바로 이동한다.
  const handleAnalysisClick = (sessionId: string) => {
    if (!userId) return;
    navigate(`/mental-health/protectee/${userId}/reports/${sessionId}`);
  };

  // 선택된 회원의 리포트 목록 페이지로 이동 (필터링)
  const handleViewAllReports = () => {
    if (!selectedUser) return;
    // userId를 쿼리 파라미터로 전달하여 해당 회원 리포트만 표시
    navigate(`/mental-health/reports?userId=${selectedUser.id}`);
  };

  const getStatusColor = (status?: string) => {
    if (!status) return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-300', dot: 'bg-gray-500' };

    // 영어 키로 직접 비교
    switch (status) {
      case 'healthy':
        return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-300', dot: 'bg-emerald-500' };
      case 'normal':
        return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300', dot: 'bg-blue-500' };
      case 'caution':
        return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-300', dot: 'bg-amber-500' };
      case 'risk':
        return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300', dot: 'bg-red-500' };
      case 'insufficient_data':
      default:
        return { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-300', dot: 'bg-gray-500' };
    }
  };

  const getStressEmoji = (stress?: string) => {
    if (!stress) return '⏳';

    // 영어 키로 직접 비교
    switch (stress) {
      case 'low': return '😌';
      case 'normal': return '😐';
      case 'high':
      case 'very_high': return '😰';
      default: return '⏳';
    }
  };

  // 선택된 사용자의 실제 차트 데이터 생성 (실제 데이터만)
  const chartData = useMemo(() => {
    if (!selectedUser || !selectedUser.hasAnalysisData) {
        return {
            labels: [],
            datasets: []
        };
    }

    // 점수가 없는 회차(측정 품질 부족)는 제외한다. 예전에는 0 으로 찍어서
    // 실제로는 없는 값이 그래프에서 급락으로 보였다.
    const userReports = recentAnalyses
        .filter(r => Number.isFinite(r.summary.mindScore))
        .slice()
        .sort((a, b) => new Date(a.measuredAt || 0).getTime() - new Date(b.measuredAt || 0).getTime())
        .slice(-7);

    if (userReports.length === 0) {
        return {
            labels: [],
            datasets: []
        };
    }

    return {
        labels: userReports.map(r => new Date(r.measuredAt || 0).toLocaleDateString(intlLocale, { month: 'short', day: 'numeric' })),
        datasets: [
            {
                label: t('mentalHealth.score.health'),
                data: userReports.map(r => Math.round(r.summary.mindScore as number)),
                borderColor: '#2966f4',
                backgroundColor: 'rgba(41, 102, 244, 0.09)',
                fill: true,
                tension: 0.34,
                pointRadius: 4,
                pointHoverRadius: 6,
                pointBorderWidth: 3,
                pointBorderColor: '#ffffff',
                pointBackgroundColor: '#2966f4',
                borderWidth: 3,
            },
        ],
    };
  }, [selectedUser, recentAnalyses, intlLocale, t]);


  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: '#071a3d',
        titleColor: '#ffffff',
        bodyColor: '#dfe8f8',
        padding: 14,
        cornerRadius: 10,
      }
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        grid: {
          color: 'rgba(112, 132, 164, 0.12)',
        },
        ticks: {
          color: '#77849a',
          font: { size: 12, weight: 600 as const },
        },
        border: { display: false },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#77849a',
          font: { size: 12, weight: 600 as const },
        },
        border: { display: false },
      }
    },
  };

  // 헤더 지표는 목록 요약에서 바로 읽는다 — 추가 요청 없이 회원을 누르는 즉시 그려진다.
  const headerHealthStatus = latestScored?.summary.decisionLabel
    || selectedUser?.healthStatus
    || 'insufficient_data';
  const headerStressLevel = latestScored?.summary.stressCategory
    || selectedUser?.stressLevel
    || 'insufficient_data';
  const headerHealthScore = formatRounded(
    latestScored?.summary.mindScore ?? selectedUser?.healthScore
  );
  const latestAnalysisDate = latestScored?.measuredAt || selectedUser?.lastAnalysisDate;
  const isDetailVisible = !!selectedUser && !isListOnlyMode;
  const hasAnalysisDetail = !!selectedUser && (selectedUser.hasAnalysisData || recentAnalyses.length > 0);
  // 상단 카드는 셋으로 유지한다 — 넷이면 사이드바에서 두 줄로 깨진다.
  // "분석 대기" 는 점수가 없는 회원 전부다 (측정 품질 부족 + 미측정).
  const pendingCount = counts ? counts.insufficient + counts.unmeasured : 0;
  const analysisRate = counts && counts.total > 0
    ? Math.round((counts.analyzed / counts.total) * 100)
    : 0;


  return (
    <PageConfig showTopNav={true} showSideNav={true}>
      {!isLoading && (
        <div
          className="health-center relative grid min-h-full overflow-x-hidden transition-[grid-template-columns] duration-300 ease-in-out md:block"
          style={{
            gridTemplateColumns: isUserListCollapsed
              ? '56px minmax(0, 1fr)'
              : '380px minmax(0, 1fr)'
          }}
        >
          {/* 좌측: 회원 리스트 */}
          <aside className={`health-directory relative min-w-0 overflow-visible md:border-r-0 ${isDetailVisible ? 'md:hidden' : ''}`}>
            {isUserListCollapsed && (
              <button
                type="button"
                aria-label={t('mentalHealth.center.expandList')}
                aria-expanded={false}
                title={t('mentalHealth.center.expandList')}
                className="health-directory__expand md:hidden"
                onClick={() => setIsUserListCollapsed(false)}
              >
                <ArrowRight className="h-[16px] w-[16px]" />
              </button>
            )}
            <div className={`${isUserListCollapsed ? 'h-full' : 'min-h-full'} overflow-hidden md:h-auto md:min-h-0 md:overflow-visible`}>
              <div className={`health-directory__content w-[380px] transition-opacity duration-200 md:w-full md:opacity-100 md:pointer-events-auto ${isUserListCollapsed ? 'pointer-events-none opacity-0' : 'opacity-100'}`}>
                {/* 헤더 */}
                <div className="health-directory__header">
                  <p>AI HEALTH ANALYTICS</p>
                  <h2>{t('mentalHealth.title')}</h2>
                  <span>{t('mentalHealth.center.subtitle')}</span>
                  <div className="flex items-center gap-2">
                    <SearchBar
                      className="min-w-0 flex-1"
                      inputParams={{
                        placeholder: t('mentalHealth.search.placeholder')
                      }}
                      onChanged={setSearch}
                    />
                    <button
                      type="button"
                      aria-label={t('mentalHealth.center.collapseList')}
                      aria-expanded={true}
                      title={t('mentalHealth.center.collapseList')}
                      className="health-directory__collapse md:hidden"
                      onClick={() => setIsUserListCollapsed(true)}
                    >
                      <ArrowLeft className="h-[16px] w-[16px]" />
                    </button>
                  </div>
                </div>

                {/* 통계 요약 */}
                <div className="health-directory__summary">
                  <div>
                    <div>
                      <strong>{counts?.total ?? 0}</strong>
                      <span>{t('mentalHealth.stats.totalMembers')}</span>
                    </div>
                    <div>
                      <strong>{counts?.analyzed ?? 0}</strong>
                      <span>{t('mentalHealth.stats.analyzed')}</span>
                    </div>
                    <div>
                      <strong>{pendingCount}</strong>
                      <span>{t('mentalHealth.filter.pending')}</span>
                    </div>
                  </div>
                  <p>
                    <span style={{ width: `${analysisRate}%` }} />
                    {t('mentalHealth.center.analysisRate', { rate: analysisRate })}
                  </p>
                </div>

                <div className="health-directory__filters" role="tablist" aria-label={t('mentalHealth.filter.label')}>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={memberFilter === 'analyzed'}
                    className={memberFilter === 'analyzed' ? 'is-active' : ''}
                    onClick={() => handleMemberFilterChange('analyzed')}
                  >
                    {t('mentalHealth.filter.scored')} <strong>{counts?.analyzed ?? 0}</strong>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={memberFilter === 'insufficient'}
                    className={memberFilter === 'insufficient' ? 'is-active' : ''}
                    onClick={() => handleMemberFilterChange('insufficient')}
                  >
                    {t('mentalHealth.filter.insufficient')} <strong>{counts?.insufficient ?? 0}</strong>
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={memberFilter === 'unmeasured'}
                    className={memberFilter === 'unmeasured' ? 'is-active' : ''}
                    onClick={() => handleMemberFilterChange('unmeasured')}
                  >
                    {t('mentalHealth.filter.unmeasured')} <strong>{counts?.unmeasured ?? 0}</strong>
                  </button>
                </div>

                {/* 회원 리스트 */}
                <div className="health-directory__list">
                  {isEmptyArray(paginatedUsers) && (
                    <div className="health-directory__empty">
                      <div>0</div>
                      <strong>
                        {debouncedSearchText
                          ? t('mentalHealth.empty.search')
                          : memberFilter === 'analyzed'
                            ? t('mentalHealth.empty.noScoredMembers')
                            : memberFilter === 'insufficient'
                              ? t('mentalHealth.empty.noInsufficientMembers')
                              : memberFilter === 'unmeasured'
                                ? t('mentalHealth.empty.noUnmeasuredMembers')
                                : t('mentalHealth.empty.noMembers')}
                      </strong>
                      <p>
                        {memberFilter === 'analyzed'
                          ? t('mentalHealth.empty.noScoredMembers.desc')
                          : t('mentalHealth.empty.noMembers')}
                      </p>
                    </div>
                  )}

                  {isNotEmptyArray(paginatedUsersWithSessions) && (
                    <div className="divide-y divide-gray-100">
                      {paginatedUsersWithSessions.map((user: UserHealthData) => {
                        const displayData = {
                          healthScore: user.healthScore,
                          healthStatus: user.healthStatus,
                          stressLevel: user.stressLevel,
                        };

                        const statusColors = getStatusColor(displayData.healthStatus);
                        const genderText = user.gender === 1 ? t('common.gender.male') : user.gender === 2 ? t('common.gender.female') : t('common.gender.none');
                        const isSelected = selectedUser?.id === user.id;

                        return (
                          <button
                            type="button"
                            key={user.id}
                            onClick={() => {
                              isAutomaticSelectionRef.current = false;
                              setIsListOnlyMode(false);
                              setUserId(user.id);
                            }}
                            className={`health-member ${isSelected ? 'is-selected' : ''}`}
                          >
                            <div className="flex items-center space-x-3">
                              {/* 상태 표시등 */}
                              <div className={`health-member__status ${statusColors.dot}`}></div>

                              {/* 아바타 */}
                              <div className="health-member__avatar">
                                {user.name.charAt(0)}
                              </div>

                              {/* 정보 */}
                              <div className="health-member__content">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center space-x-2">
                                    <span className="font-bold text-gray-900 truncate">{user.name}</span>
                                    {user.hasBackendData && <span className="health-member__synced" title={t('mentalHealth.center.synced')}>SYNC</span>}
                                  </div>
                                  {user.hasAnalysisData ? (
                                    <span className={`text-xs px-2 py-1 rounded-full ${statusColors.bg} ${statusColors.text} font-semibold`}>
                                      {displayData.healthStatus ? t(`mentalHealth.status.${displayData.healthStatus}`) : t('mentalHealth.status.pending')}
                                    </span>
                                  ) : (
                                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-500 font-semibold">
                                      {t('mentalHealth.status.pending')}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2 text-xs text-gray-600">
                                  <span>{genderText}</span>
                                  {user.age && (
                                    <>
                                      <span>•</span>
                                      <span>{t('dashboard.notification.age', { count: user.age })}</span>
                                    </>
                                  )}
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                  <span className="text-xs text-gray-500">
                                    {getStressEmoji(displayData.stressLevel)} {displayData.stressLevel ? t(`mentalHealth.stress.${displayData.stressLevel}`) : t('mentalHealth.status.pending')}
                                  </span>
                                  {user.hasAnalysisData && (
                                    <span className="health-member__score">
                                      {Number.isFinite(displayData.healthScore)
                                        ? `${displayData.healthScore}${t('mentalHealth.score.points')}`
                                        : t('mentalHealth.center.serverAnalysisCount', { count: user.sessionCount || 1 })}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* 페이지네이션 */}
                {totalPages > 1 && (
                  <div className="p-4 border-t border-gray-200">
                    <Pagination
                      page_size={PAGE_SIZE}
                      current_page={currentPage}
                      total_page={totalPages}
                      fetchPage={handlePageChange}
                    />
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* 우측: 상세 정보 */}
          <main className={`health-center__detail min-h-full ${isDetailVisible ? '' : 'md:hidden'}`}>
            <div className="hidden md:flex sticky top-0 z-[1] items-center gap-[10px] border-b border-solid border-gray2 bg-white px-[12px] py-[10px]">
              <button
                className="min-h-[36px] rounded-[5px] border border-solid border-gray2 px-[12px] text-[14px] font-medium"
                onClick={() => {
                  setIsListOnlyMode(true);
                  setUserId(0);
                }}
              >
                {t('mentalHealth.reportDetail.backToList')}
              </button>
            </div>
            {selectedUser ? (
              hasAnalysisDetail ? (
                // 분석 데이터가 있는 경우 (로컬 또는 백엔드)
                <div className="health-profile-view">
                  {/* 헤더 */}
                  <div className="health-profile">
                    <div className="flex items-center justify-between md:flex-col md:items-start md:gap-5">
                      <div className="flex items-center space-x-6 sm:space-x-3">
                        <div className="health-profile__avatar">
                          {selectedUser.name.charAt(0)}
                        </div>
                        <div>
                          <p>AI ANALYSIS SUBJECT</p>
                          <h1>{selectedUser.name}</h1>
                          <div className="health-profile__meta">
                            <span>{selectedUser.gender === 1 ? t('common.gender.male') : selectedUser.gender === 2 ? t('common.gender.female') : t('common.gender.none')}</span>
                            {selectedUser.age && (
                              <>
                                <span>•</span>
                                <span>{t('dashboard.notification.age', { count: selectedUser.age })}</span>
                              </>
                            )}
                            {latestAnalysisDate && (
                              <>
                                <span>•</span>
                                <span>{t('mentalHealth.lastAnalysis')}: {new Date(latestAnalysisDate).toLocaleDateString(intlLocale)}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="health-profile__score">
                        <span>AI HEALTH SCORE</span>
                        <strong>{headerHealthScore}</strong>
                        <small>/ 100</small>
                      </div>
                    </div>
                  </div>

                  {/* 주요 지표 */}
                  <div className="health-profile__metrics">
                    {/* 상태 */}
                    <div className={`health-kpi health-kpi--status health-kpi--${headerHealthStatus}`}>
                      <div className="flex items-center justify-between mb-4">
                        <span>{t('mentalHealth.currentStatus')}</span>
                        <i>ST</i>
                      </div>
                      <strong>
                        {headerHealthStatus ? t(`mentalHealth.status.${headerHealthStatus}`) : t('mentalHealth.status.pending')}
                      </strong>
                      <p>
                        {t('mentalHealth.currentStatus.desc')}
                      </p>
                    </div>

                    {/* 스트레스 */}
                    <div className="health-kpi health-kpi--stress">
                      <div className="flex items-center justify-between mb-4">
                        <span>{t('mentalHealth.stress.title')}</span>
                        <i>HRV</i>
                      </div>
                      <strong>
                        {headerStressLevel ? t(`mentalHealth.stress.${headerStressLevel}`) : t('mentalHealth.status.pending')}
                      </strong>
                      <p>
                        {t('mentalHealth.stress.desc')}
                      </p>
                    </div>
                    <div className="health-kpi health-kpi--sessions">
                      <div><span>{t('mentalHealth.center.cumulativeAnalysis')}</span><i>AI</i></div>
                      <strong>{selectedUser.sessionCount || recentAnalyses.length}{t('common.count')}</strong>
                      <p>{t('mentalHealth.center.completedSessions')}</p>
                    </div>
                    <div className="health-kpi health-kpi--updated">
                      <div><span>{t('mentalHealth.center.latestMeasurement')}</span><i>TM</i></div>
                      <strong>{latestAnalysisDate ? new Date(latestAnalysisDate).toLocaleDateString(intlLocale) : '-'}</strong>
                      <p>{t('mentalHealth.center.latestBasis')}</p>
                    </div>
                  </div>

                  {/* 건강 추이 차트 */}
                  <div className="health-panel health-trend">
                    <div className="health-panel__heading">
                      <div>
                        <span>AI HEALTH TREND</span>
                        <h3>{t('mentalHealth.chart.trend')}</h3>
                        <p>{t('mentalHealth.chart.trend.desc')}</p>
                      </div>
                      {chartData.labels.length > 0 && (
                        <BorderButton
                          text={t('mentalHealth.button.viewDetailAnalysis')}
                          onClick={handleViewDetailReport}
                        />
                      )}
                    </div>
                    {chartData.labels.length > 0 ? (
                      <div className="health-trend__chart">
                        <Line data={chartData} options={chartOptions} />
                      </div>
                    ) : (
                      <div className="health-panel__empty">
                        <div>
                          <span>CH</span>
                          <strong>{t('mentalHealth.chart.insufficient')}</strong>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* HRV 기반 증상 예측 */}
                  <div className="health-panel health-symptoms">
                    <div className="health-panel__heading">
                      <div><span>AI BIOMETRIC SIGNAL</span><h3>{t('mentalHealth.symptom.prediction')}</h3><p>{t('mentalHealth.center.riskDescription')}</p></div>
                    </div>
                    {latestReportDetail?.report?.symptomPredictions ? (
                      <>
                        <div className="health-symptoms__grid">
                          <SymptomPredictionCard prediction={latestReportDetail.report.symptomPredictions.stress} />
                          <SymptomPredictionCard prediction={latestReportDetail.report.symptomPredictions.cardiovascular} />
                          <SymptomPredictionCard prediction={latestReportDetail.report.symptomPredictions.breathing} />
                          <SymptomPredictionCard prediction={latestReportDetail.report.symptomPredictions.depression} />
                          <SymptomPredictionCard prediction={latestReportDetail.report.symptomPredictions.pain} />
                          <SymptomPredictionCard prediction={latestReportDetail.report.symptomPredictions.bloodPressure} />
                        </div>
                        <div className="health-symptoms__notice">
                          <p dangerouslySetInnerHTML={{ __html: t('mentalHealth.symptom.disclaimer') }} />
                        </div>
                      </>
                    ) : (
                      <div className="health-panel__empty">
                        <span>HRV</span>
                        <strong>{t('mentalHealth.symptom.noData')}</strong>
                      </div>
                    )}
                  </div>

                  {/* 최근 활동 */}
                  <div className="health-panel health-history">
                    <div className="health-panel__heading">
                      <div><span>AI ANALYSIS HISTORY</span><h3>{t('mentalHealth.recentAnalysis')}</h3><p>{t('mentalHealth.center.historyDescription')}</p></div>
                    </div>
                    {recentAnalyses.length > 0 ? (
                      <>
                        <div className="health-history__list">
                          {recentAnalyses.map((analysis) => {
                            const measuredAt = new Date(analysis.measuredAt || analysis.computedAt || 0);
                            const hasScore = Number.isFinite(analysis.summary.mindScore);
                            const label = analysis.summary.decisionLabel;
                            const statusColors = getStatusColor(label || undefined);
                            return (
                              <button
                                type="button"
                                key={analysis.sessionId}
                                className="health-history__item"
                                disabled={!hasScore}
                                onClick={() => handleAnalysisClick(analysis.sessionId)}
                              >
                                <div className="flex items-center space-x-4">
                                  <div className="health-history__source">SV</div>
                                  <div>
                                    <div className="font-semibold text-gray-900">
                                      {measuredAt.toLocaleDateString(intlLocale)}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      {measuredAt.toLocaleTimeString(intlLocale, { hour: '2-digit', minute: '2-digit' })}
                                      <span className="ml-2 text-blue-600">• {t('mentalHealth.center.server')}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-4">
                                  {hasScore ? (
                                    <>
                                      <div className="text-right">
                                        <div className="text-2xl font-bold text-gray-900">
                                          {formatRounded(analysis.summary.mindScore)}
                                        </div>
                                        <div className="text-xs text-gray-600">{t('mentalHealth.score.points')}</div>
                                      </div>
                                      <div className={`px-3 py-1 rounded-lg text-sm font-semibold ${statusColors.bg} ${statusColors.text}`}>
                                        {t(`mentalHealth.status.${label}`)}
                                      </div>
                                    </>
                                  ) : (
                                    // 측정은 했으나 점수가 없는 회차. 눌러 들어가면 사유를 볼 수 있다.
                                    <div className="health-history__load is-insufficient">
                                      {t('mentalHealth.loading.insufficient')}
                                    </div>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        <div className="mt-4">
                          <BorderButton
                            text={t('mentalHealth.recentAnalysis.viewAll')}
                            onClick={handleViewAllReports}
                            className="w-full"
                          />
                        </div>
                      </>
                    ) : (
                      <div className="health-panel__empty">
                        <span>LG</span>
                        <strong>{t('mentalHealth.recentAnalysis.empty')}</strong>
                      </div>
                    )}
                  </div>

                  {/* 액션 버튼들 */}
                  <div className="health-profile__actions">
                    <SolidButton
                      text={t('mentalHealth.button.viewDetailReport')}
                      onClick={handleViewDetailReport}
                      className="health-profile__primary-action"
                    />
                    <BorderButton
                      text={t('mentalHealth.button.newAnalysis')}
                      onClick={() => sessionModalRef.current?.show()}
                      className="health-profile__secondary-action"
                    />
                  </div>
                </div>
              ) : (
                // 분석 데이터가 없는 경우
                <div className="health-empty-state">
                  <div className="health-empty-state__card">
                    <div className="health-empty-state__icon">BCG</div>
                    <span className="health-empty-state__eyebrow">AI ANALYSIS REQUIRED</span>
                    <h3>{t('mentalHealth.noData.title')}</h3>
                    <p>
                      {t('mentalHealth.noData.desc1', { name: selectedUser.name })}
                    </p>
                    <p>
                      {t('mentalHealth.noData.desc2')}
                    </p>

                    {/* 회원 정보 */}
                    <div className="health-empty-state__member">
                      <div className="flex items-center space-x-4">
                        <div className="health-empty-state__avatar">
                          {selectedUser.name.charAt(0)}
                        </div>
                        <div>
                          <strong>{selectedUser.name}</strong>
                          <span>
                            {selectedUser.gender === 1 ? t('common.gender.male') : selectedUser.gender === 2 ? t('common.gender.female') : t('common.gender.none')}
                            {selectedUser.age && ` • ${t('dashboard.notification.age', { count: selectedUser.age })}`}
                          </span>
                        </div>
                      </div>
                    </div>

                    <SolidButton
                      text={t('mentalHealth.button.uploadBCG')}
                      onClick={() => sessionModalRef.current?.show()}
                      className="health-empty-state__action"
                    />
                  </div>
                </div>
              )
            ) : (
              <div className="health-empty-state health-empty-state--select">
                <div className="health-empty-state__card">
                  <div className="health-empty-state__icon">ID</div>
                  <span className="health-empty-state__eyebrow">SELECT A PROTECTEE</span>
                  <h3>{t('mentalHealth.empty.selectMember')}</h3>
                  <p>{t('mentalHealth.empty.selectMember.desc')}</p>
                </div>
              </div>
            )}
          </main>
        </div>
      )}

      {/* BCG 세션 모달 (실시간 데이터 수집) */}
      <BCGSessionModal
        ref={sessionModalRef}
        userId={selectedUser?.id}
        userName={selectedUser?.name}
        onSuccess={handleUploadSuccess}
      />
    </PageConfig>
  );
};

export default MentalHealthDashboard;
